import { create } from 'zustand'
import { Memory } from '@/types'
import * as api from '@/services/api'

interface MemoryState {
  memories: Memory[]
  isLoading: boolean
  isSubmitting: boolean
  error: string | null
  fetchMemories: (orgId: string) => Promise<void>
  addMemory: (orgId: string, memoryData: { content: string }) => Promise<void>
  updateMemory: (
    orgId: string,
    memoryId: string,
    memoryData: { content: string },
  ) => Promise<void>
  deleteMemory: (orgId: string, memoryId: string) => Promise<void>
}

const useMemoryStore = create<MemoryState>((set, get) => ({
  memories: [],
  isLoading: false,
  isSubmitting: false,
  error: null,

  fetchMemories: async (orgId: string) => {
    set({ isLoading: true, error: null })
    try {
      const memories = await api.getMemories(orgId)
      set({ memories, isLoading: false })
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to fetch memories.'
      set({ error: errorMessage, isLoading: false, memories: [] })
    }
  },

  addMemory: async (orgId: string, memoryData: { content: string }) => {
    set({ isSubmitting: true, error: null })
    try {
      await api.addMemory(orgId, memoryData)
      await get().fetchMemories(orgId)
      set({ isSubmitting: false })
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to add memory.'
      set({ error: errorMessage, isSubmitting: false })
      throw error
    }
  },

  updateMemory: async (
    orgId: string,
    memoryId: string,
    memoryData: { content: string },
  ) => {
    set({ isSubmitting: true, error: null })
    try {
      const originalMemory = get().memories.find((m) => m.id === memoryId)
      if (!originalMemory) {
        throw new Error('Memory to update not found in local state.')
      }

      const payload: Memory = {
        ...originalMemory,
        content: memoryData.content,
      }

      await api.updateMemory(orgId, memoryId, payload)
      await get().fetchMemories(orgId)
      set({ isSubmitting: false })
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to update memory.'
      set({ error: errorMessage, isSubmitting: false })
      throw error
    }
  },

  deleteMemory: async (orgId: string, memoryId: string) => {
    set({ isSubmitting: true, error: null })
    try {
      await api.deleteMemory(orgId, memoryId)
      await get().fetchMemories(orgId)
      set({ isSubmitting: false })
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to delete memory.'
      set({ error: errorMessage, isSubmitting: false })
      throw error
    }
  },
}))

export default useMemoryStore
